import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import FeedbackModal from './FeedbackModal';
import './DebateRoomNew.css';

const API = process.env.REACT_APP_BACKEND_URL;

const DebateRoomNew = () => {
  const navigate = useNavigate();
  const { id: debateId } = useParams();
  
  // Core Debate State
  const [debate, setDebate] = useState(null);
  const [debateArguments, setDebateArguments] = useState([]);
  const [newArgument, setNewArgument] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  // Timer State
  const [totalTimeRemaining, setTotalTimeRemaining] = useState(0);
  const [totalTimeStarted, setTotalTimeStarted] = useState(false);
  const [timeRemainingA, setTimeRemainingA] = useState(0);
  const [timeRemainingB, setTimeRemainingB] = useState(0);
  const [currentTurn, setCurrentTurn] = useState(null);
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const timersInitializedRef = useRef(false);
  
  // UI State
  const [inputMode, setInputMode] = useState('keyboard');
  const [textareaTheme, setTextareaTheme] = useState('dark');
  const [opponentName, setOpponentName] = useState('Opponent');
  const [opponentModel, setOpponentModel] = useState('');
  const [playerAName, setPlayerAName] = useState('Protagonist A');
  const [playerBName, setPlayerBName] = useState('Protagonist B');
  const [argumentsSubmitted, setArgumentsSubmitted] = useState(false);
  const [hasStartedTyping, setHasStartedTyping] = useState(false);
  const [expandedAnalysis, setExpandedAnalysis] = useState(null); // Track which argument shows analysis
  const [analysisFontSize, setAnalysisFontSize] = useState(14); // Font size for analysis (px)
  const [bubbleFontSize, setBubbleFontSize] = useState(16); // Font size for bubble content (px)
  const [shouldResetTimer, setShouldResetTimer] = useState(false); // Flag to reset timer after submit
  const [isRecording, setIsRecording] = useState(false); // Speech recording state
  const [isAnalyzing, setIsAnalyzing] = useState(false); // Loading state for AI analysis
  
  // Feedback Modal State
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [selectedArgumentForFeedback, setSelectedArgumentForFeedback] = useState(null);
  
  // AI State
  const [aiStatus, setAiStatus] = useState({ show: false, phase: 'thinking', remaining: 0, total: 0 });
  
  // User State (for pause feature)
  const [userTier, setUserTier] = useState('free');
  const [isPausing, setIsPausing] = useState(false);
  const [showPauseModal, setShowPauseModal] = useState(false);
  const [resumeLink, setResumeLink] = useState('');
  const [daysRemaining, setDaysRemaining] = useState(7);
  const [showResumeOverlay, setShowResumeOverlay] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false); // Track if link was copied
  
  // Asset URLs
  const backgroundUrl = 'https://customer-assets.emergentagent.com/job_debatefy-freemium/artifacts/m0t4gk3h_dabatefy_dabateroom_background_ND02.jpg';
  const elementAUrl = 'https://customer-assets.emergentagent.com/job_debatefy-freemium/artifacts/acxtk31p_dabatefy_dabateroom_Element_A_ND02.png';
  
  // Refs
  const textareaRef = useRef(null); // Textarea ref for auto-grow
  const recognitionRef = useRef(null); // Speech recognition ref
  const inputAreaRef = useRef(null); // Input area ref for dynamic spacing
  const argumentsContainerRef = useRef(null); // Arguments container ref
  
  // Dedizierte Timer-Reset-Funktion
  const triggerTimerReset = () => {
    setShouldResetTimer(true);
  };

  // Format time helper
  const formatTime = (seconds) => {
    if (seconds === null || seconds === undefined) return '00:00';
    const absSeconds = Math.abs(seconds);
    const mins = Math.floor(absSeconds / 60);
    const secs = absSeconds % 60;
    const sign = seconds < 0 ? '-' : '';
    return `${sign}${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  // Get time class for hue-rotate
  const getTimeClass = (time) => {
    if (time === null || time === undefined) return '';
    if (time < 0) return 'overtime';
    if (time > 0) return 'running';
    return '';
  };

  // Get score class for hue-rotate
  const getScoreClass = (score) => {
    if (score > 0) return 'positive';
    if (score < 0) return 'negative';
    return '';
  };

  // Initialize debate and polling
  useEffect(() => {
    timersInitializedRef.current = false;
    
    // Scroll to top when debate room loads
    window.scrollTo(0, 0);
    
    fetchDebate();
    fetchArguments();
    const interval = setInterval(() => {
      // Don't poll if pause modal is showing
      if (!showPauseModal) {
        fetchDebate();
        fetchArguments();
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [debateId, showPauseModal]);

  // Initialize timers
  useEffect(() => {
    if (debate && debate.argument_time_limit && !timersInitializedRef.current) {
      // Calculate total time remaining
      if (debate.total_time_started_at) {
        const startedAt = new Date(debate.total_time_started_at);
        const now = new Date();
        const elapsedSeconds = Math.floor((now - startedAt) / 1000);
        const totalSeconds = (debate.total_time_limit * 60) - elapsedSeconds;
        setTotalTimeRemaining(Math.max(0, totalSeconds));
        setTotalTimeStarted(true);
      } else {
        const totalSeconds = debate.total_time_limit * 60 || 0;
        setTotalTimeRemaining(totalSeconds);
        setTotalTimeStarted(false);
      }
      
      // Per-argument time
      const perArgumentTime = debate.argument_time_limit || 0;
      setTimeRemainingA(perArgumentTime);
      setTimeRemainingB(perArgumentTime);
      
      timersInitializedRef.current = true;
      console.log('Timers initialized');
    }
    
    // Sync total timer
    if (debate && debate.total_time_started_at && timersInitializedRef.current) {
      const startedAt = new Date(debate.total_time_started_at);
      const now = new Date();
      const elapsedSeconds = Math.floor((now - startedAt) / 1000);
      const totalSeconds = (debate.total_time_limit * 60) - elapsedSeconds;
      const syncedTime = Math.max(0, totalSeconds);
      
      if (Math.abs(totalTimeRemaining - syncedTime) > 2) {
        setTotalTimeRemaining(syncedTime);
      }
      
      if (!totalTimeStarted) {
        setTotalTimeStarted(true);
      }
    }
  }, [debate]);

  // Total Time Countdown
  useEffect(() => {
    if (!totalTimeStarted) return;

    const totalTimer = setInterval(() => {
      setTotalTimeRemaining(prev => {
        const newTime = Math.max(0, prev - 1);
        if (newTime === 0) {
          handleTotalTimeExpired();
        }
        return newTime;
      });
    }, 1000);

    return () => clearInterval(totalTimer);
  }, [totalTimeStarted]);

  // Per-Argument Timer
  useEffect(() => {
    if (!isTimerRunning || !currentTurn || !debate?.id) return;

    const timer = setInterval(() => {
      if (currentTurn === 'a') {
        setTimeRemainingA(prev => {
          const newTime = Math.max(0, prev - 1);
          if (newTime === 0) {
            setIsTimerRunning(false);
            toast.warning('⏰ Time is up for this argument, Protagonist A!');
          }
          return newTime;
        });
      } else if (currentTurn === 'b') {
        setTimeRemainingB(prev => {
          const newTime = Math.max(0, prev - 1);
          if (newTime === 0) {
            setIsTimerRunning(false);
            toast.warning('⏰ Time is up for this argument, Protagonist B!');
          }
          return newTime;
        });
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [isTimerRunning, currentTurn, debate?.id]);

  // Auto-grow textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = '150px';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  }, [newArgument]);

  // Reset timer after argument submission
  useEffect(() => {
    if (shouldResetTimer && debate?.argument_time_limit) {
      const myRole = localStorage.getItem('participant_role');
      const timerValue = debate.argument_time_limit;
      
      if (myRole === 'protagonist_a') {
        setTimeRemainingA(timerValue);
      } else if (myRole === 'protagonist_b') {
        setTimeRemainingB(timerValue);
      }
      
      setShouldResetTimer(false);
    }
  }, [shouldResetTimer, debate?.argument_time_limit]);

  // AI Auto-Play Logic
  useEffect(() => {
    if (!debate || !debate.is_ai_opponent) return;
    
    const myRole = localStorage.getItem('participant_role');
    const isMyTurn = (currentTurn === 'a' && myRole === 'protagonist_a') || 
                     (currentTurn === 'b' && myRole === 'protagonist_b');
    
    if (!isMyTurn && currentTurn && !submitting) {
      const aiTurnRole = currentTurn;
      const aiSessionId = aiTurnRole === 'a' ? debate.session_a_id : debate.session_b_id;
      
      if (aiSessionId && aiSessionId.startsWith('ai_opponent_')) {
        setAiStatus({ show: true, phase: 'generating', remaining: 0, total: 0 });
        
        const pollInterval = setInterval(async () => {
          try {
            const response = await axios.get(`${API}/api/debate/${debateId}`);
            const latestDebate = response.data;
            
            if (latestDebate.current_turn !== currentTurn) {
              clearInterval(pollInterval);
              setAiStatus({ show: false, phase: 'thinking', remaining: 0, total: 0 });
              fetchArguments();
            }
          } catch (error) {
            console.error('Error polling for AI response:', error);
          }
        }, 1000);

        setTimeout(() => clearInterval(pollInterval), 60000);
      }
    }
  }, [currentTurn, debate, submitting]);

  // Initialize Speech Recognition
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      const recognition = new SpeechRecognition();
      
      recognition.continuous = false;
      recognition.interimResults = false;
      recognition.lang = 'de-DE';
      recognition.maxAlternatives = 1;
      
      let lastProcessedIndex = 0;
      
      recognition.onresult = (event) => {
        for (let i = lastProcessedIndex; i < event.results.length; i++) {
          if (event.results[i].isFinal) {
            const transcript = event.results[i][0].transcript;
            setNewArgument(prev => {
              const newText = prev + (prev ? ' ' : '') + transcript.trim();
              return newText;
            });
            lastProcessedIndex = i + 1;
          }
        }
      };
      
      recognition.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        if (event.error === 'no-speech') {
          toast.error('Keine Sprache erkannt. Bitte sprechen Sie ins Mikrofon.');
        } else if (event.error === 'not-allowed') {
          toast.error('Mikrofon-Zugriff verweigert. Bitte erlauben Sie den Zugriff.');
        } else if (event.error === 'aborted') {
          // Silently ignore aborted errors
        } else {
          toast.error('Fehler bei der Spracherkennung: ' + event.error);
        }
        setIsRecording(false);
      };
      
      recognition.onend = () => {
        if (isRecording) {
          lastProcessedIndex = 0;
          try {
            recognition.start();
          } catch (e) {
            console.error('Error restarting recognition:', e);
          }
        }
      };
      
      recognitionRef.current = recognition;
    } else {
      console.warn('Speech Recognition not supported');
    }
    
    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (e) {
          // Ignore errors on cleanup
        }
      }
    };
  }, [isRecording]);

  // Arguments container bottom is now set via CSS only
  // The container is inside the 0.7 scaled wrapper, so CSS bottom value 
  // is in the original 1920x1080 coordinate system

  // Handle microphone button click
  const handleMicrophoneClick = () => {
    if (!recognitionRef.current) {
      toast.error('Spracherkennung wird von diesem Browser nicht unterstützt.');
      return;
    }
    
    if (isRecording) {
      // Stop recording
      recognitionRef.current.stop();
      setIsRecording(false);
      toast.success('Aufnahme gestoppt');
    } else {
      // Start recording
      try {
        recognitionRef.current.start();
        setIsRecording(true);
        setHasStartedTyping(true);
        setIsTimerRunning(true);
        toast.info('🎤 Sprachaufnahme gestartet...');
      } catch (error) {
        console.error('Error starting speech recognition:', error);
        toast.error('Fehler beim Starten der Aufnahme');
      }
    }
  };

  // Fetch debate data
  const fetchDebate = async () => {
    try {
      const response = await axios.get(`${API}/api/debate/${debateId}`);
      const debateData = response.data;
      setDebate(debateData);
      
      // Check if debate is paused - show resume overlay (but not if we just paused it ourselves)
      if (debateData.status === 'paused' && !showPauseModal) {
        setShowResumeOverlay(true);
        // Restore saved timer values
        if (debateData.time_remaining_a) setTimeRemainingA(debateData.time_remaining_a);
        if (debateData.time_remaining_b) setTimeRemainingB(debateData.time_remaining_b);
        if (debateData.total_time_remaining) setTotalTimeRemaining(debateData.total_time_remaining);
        return; // Don't continue setup until resumed
      }
      
      if (debateData.current_turn) {
        setCurrentTurn(debateData.current_turn);
      }
      
      // Set player names
      const myRole = localStorage.getItem('participant_role');
      if (myRole === 'protagonist_a') {
        setPlayerAName(localStorage.getItem('participant_name') || 'You');
      } else if (myRole === 'protagonist_b') {
        setPlayerBName(localStorage.getItem('participant_name') || 'You');
      }
      
      // Fetch opponent name
      const opponentSessionId = myRole === 'protagonist_a' ? debateData.session_b_id : debateData.session_a_id;
      if (opponentSessionId) {
        if (debateData.is_ai_opponent && opponentSessionId.startsWith('ai_opponent_')) {
          const aiStyle = debateData.ai_style || "AI-Opponent";
          
          // Parse AI name and model - NEW: Two-line format
          // Expected format: "Der Pragmatiker (Gemini)" or "🤖 Der Pragmatiker (Gemini)"
          const cleanName = aiStyle.replace(/^🤖\s*/, ''); // Remove emoji
          
          // Extract name and model
          const match = cleanName.match(/^(.+?)\s*\((.+?)\)$/);
          if (match) {
            setOpponentName(match[1].trim()); // e.g., "Der Pragmatiker"
            setOpponentModel(match[2].trim()); // e.g., "Gemini"
            
            // Set the AI name in the correct player slot
            if (myRole === 'protagonist_a') {
              setPlayerBName(match[1].trim()); // AI is Protagonist B
            } else if (myRole === 'protagonist_b') {
              setPlayerAName(match[1].trim()); // AI is Protagonist A
            }
          } else {
            setOpponentName(cleanName);
            setOpponentModel('');
            
            // Set the AI name in the correct player slot
            if (myRole === 'protagonist_a') {
              setPlayerBName(cleanName);
            } else if (myRole === 'protagonist_b') {
              setPlayerAName(cleanName);
            }
          }
        } else {
          try {
            const sessionResponse = await axios.get(`${API}/api/session/${opponentSessionId}`);
            const opName = sessionResponse.data.participant_name || 'Opponent';
            setOpponentName(opName);
            setOpponentModel('');
            
            // Set human opponent name in the correct player slot
            if (myRole === 'protagonist_a') {
              setPlayerBName(opName);
            } else if (myRole === 'protagonist_b') {
              setPlayerAName(opName);
            }
          } catch (error) {
            console.error('Error fetching opponent session:', error);
          }
        }
      }
      
      // Don't auto-expand - wait for first keystroke
    } catch (error) {
      console.error('Error fetching debate:', error);
    }
  };

  // Fetch arguments
  const fetchArguments = async () => {
    try {
      const response = await axios.get(`${API}/api/debate/${debateId}/arguments`);
      setDebateArguments(response.data);
    } catch (error) {
      console.error('Error fetching arguments:', error);
    }
  };

  // Handle total time expired
  const handleTotalTimeExpired = async () => {
    try {
      toast.error('⏰ Total debate time has expired!');
      
      let winner = null;
      if (debate.score_a > debate.score_b) {
        winner = 'a';
        toast.success('🏆 Protagonist A wins by higher score!');
      } else if (debate.score_b > debate.score_a) {
        winner = 'b';
        toast.success('🏆 Protagonist B wins by higher score!');
      } else {
        toast.info('🤝 Debate ends in a draw!');
      }
      
      await axios.patch(`${API}/api/debate/${debateId}/complete`, {
        winner: winner,
        completion_reason: 'total_time_expired'
      });
      
      setTimeout(() => {
        navigate(`/archive/view/${debateId}`);
      }, 3000);
    } catch (error) {
      console.error('Error handling total time expiration:', error);
      toast.error('Failed to end debate.');
    }
  };

  // Submit argument
  const handleSubmitArgument = async () => {
    if (!newArgument.trim()) {
      toast.error('Please enter an argument');
      return;
    }

    const myRole = localStorage.getItem('participant_role');
    const mySessionId = localStorage.getItem('session_id');

    if (!myRole || !mySessionId) {
      toast.error('Session error. Please rejoin the debate.');
      return;
    }

    // Check if it's my turn
    const isMyTurn = (currentTurn === 'a' && myRole === 'protagonist_a') || 
                     (currentTurn === 'b' && myRole === 'protagonist_b');

    if (!isMyTurn) {
      toast.error("It's not your turn!");
      return;
    }

    try {
      setSubmitting(true);
      setIsAnalyzing(true); // Show loading bar
      setIsTimerRunning(false); // Stop timer during submission
      
      const response = await axios.post(`${API}/api/debate/${debateId}/submit-argument`, {
        debate_id: debateId,
        session_id: mySessionId,
        content: newArgument,
        thinking_time_seconds: 0,
        typing_time_seconds: 0
      });
      
      if (response.status === 200 || response.status === 201) {
        toast.success('Argument submitted!');
        
        // Clear input and reset states IMMEDIATELY for reactive UI
        setNewArgument(''); // Clear input FIRST
        setHasStartedTyping(false); // Reset typing flag
        setIsTimerRunning(false); // Stop timer
        
        // Start total time on first argument submission
        if (!totalTimeStarted) {
          setTotalTimeStarted(true);
        }
        
        // Fetch updated debate and arguments
        await fetchDebate();
        await fetchArguments();
        
        // Hide loading bar after data is fetched
        setIsAnalyzing(false);
        
        // AFTER fetch, trigger timer reset with fresh debate data
        triggerTimerReset();
      }
    } catch (error) {
      console.error('Error submitting argument:', error);
      
      // Handle error message - convert objects to strings
      let errorMessage = 'Failed to submit argument';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          errorMessage = detail.map(err => err.msg || JSON.stringify(err)).join(', ');
        } else {
          errorMessage = JSON.stringify(detail);
        }
      }
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
      setIsAnalyzing(false); // Hide loading bar on error too
    }
  };

  // Handle "Stop Well Argued"
  const handleStopWellArgued = async () => {
    try {
      const myRole = localStorage.getItem('participant_role');
      
      if (!debate) {
        toast.error('Debate data not loaded');
        return;
      }

      // Determine winner
      let winner = null;
      if (debate.score_a > debate.score_b) {
        winner = 'a';
        toast.success('🏆 Protagonist A wins!');
      } else if (debate.score_b > debate.score_a) {
        winner = 'b';
        toast.success('🏆 Protagonist B wins!');
      } else {
        toast.info('🤝 Debate ends in a draw!');
      }

      await axios.patch(`${API}/api/debate/${debateId}/complete`, {
        winner: winner,
        completion_reason: 'well_argued_stop'
      });

      setTimeout(() => {
        navigate(`/archive/view/${debateId}`);
      }, 2000);
    } catch (error) {
      console.error('Error stopping debate:', error);
      toast.error('Failed to stop debate');
    }
  };

  // Handle Pause Debate (Pro/Unlimited Feature)
  const handlePauseDebate = async () => {
    // Check if user is Pro/Unlimited
    if (!['pro', 'unlimited'].includes(userTier)) {
      toast.error('⭐ Upgrade to Pro to pause debates', {
        description: 'Pausing debates is a Pro feature. Upgrade to continue your debate later.',
        action: {
          label: 'Upgrade',
          onClick: () => navigate('/pricing')
        }
      });
      return;
    }

    try {
      setIsPausing(true);
      const token = localStorage.getItem('user_session_token') || 
                    localStorage.getItem('admin_session_token') || 
                    localStorage.getItem('token');
      
      if (!token) {
        toast.error('Please log in to pause debates');
        return;
      }

      // Send current timer values to backend
      const response = await axios.post(
        `${API}/api/debate/${debateId}/pause`,
        {
          time_remaining_a: timeRemainingA,
          time_remaining_b: timeRemainingB,
          total_time_remaining: totalTimeRemaining
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Generate resume link
      const resumeLink = `${window.location.origin}/debate/${debateId}`;
      
      // Show pause modal with link - don't auto-copy, wait for user action
      setShowPauseModal(true);
      setResumeLink(resumeLink);
      setDaysRemaining(response.data.days_remaining);
      setLinkCopied(false); // Reset copied state
      
      toast.info('⏸️ Debate paused!', {
        description: 'Please copy the resume link before leaving.',
        duration: 5000
      });

    } catch (error) {
      console.error('Error pausing debate:', error);
      const message = error.response?.data?.detail || 'Failed to pause debate';
      toast.error(message);
    } finally {
      setIsPausing(false);
    }
  };

  // Handle Resume Debate
  const handleResumeDebate = async () => {
    try {
      const token = localStorage.getItem('user_session_token') || 
                    localStorage.getItem('admin_session_token') || 
                    localStorage.getItem('token');
      
      const response = await axios.post(
        `${API}/api/debate/${debateId}/resume`,
        {},
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );

      // Restore timer values from response
      if (response.data.time_remaining_a !== undefined) {
        setTimeRemainingA(response.data.time_remaining_a);
      }
      if (response.data.time_remaining_b !== undefined) {
        setTimeRemainingB(response.data.time_remaining_b);
      }
      if (response.data.total_time_remaining !== undefined) {
        setTotalTimeRemaining(response.data.total_time_remaining);
      }

      // Hide overlay and continue
      setShowResumeOverlay(false);
      toast.success('▶️ Debate resumed!');
      
      // Refetch debate to get updated status
      fetchDebate();
      fetchArguments();

    } catch (error) {
      console.error('Error resuming debate:', error);
      const message = error.response?.data?.detail || 'Failed to resume debate';
      toast.error(message);
    }
  };

  // Fetch user tier on mount
  useEffect(() => {
    const fetchUserTier = async () => {
      // Check for token in various localStorage keys
      const token = localStorage.getItem('user_session_token') || 
                    localStorage.getItem('admin_session_token') || 
                    localStorage.getItem('token');
      
      if (!token) {
        setUserTier('guest');
        return;
      }
      
      try {
        const response = await axios.get(`${API}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        // Note: response is { user: { subscription_tier: ... } }
        const tier = response.data.user?.subscription_tier || response.data.subscription_tier || 'free';
        console.log('User tier loaded:', tier);
        setUserTier(tier);
      } catch (error) {
        console.error('Error fetching user tier:', error);
        setUserTier('free');
      }
    };
    
    fetchUserTier();
  }, []);

  if (!debate) {
    return (
      <div className="debate-room-container">
        <img 
          src={backgroundUrl} 
          alt="Debate Room Background" 
          className="debate-room-background"
        />
        <div style={{ 
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          color: '#EDEDED', 
          fontSize: '32px', 
          fontFamily: 'Bebas Neue',
          textAlign: 'center',
          zIndex: 2000
        }}>
          <div>Loading debate...</div>
          <div style={{ fontSize: '18px', marginTop: '20px', opacity: 0.7 }}>
            Debate ID: {debateId}
          </div>
          <button 
            onClick={() => navigate('/')}
            style={{
              marginTop: '30px',
              padding: '15px 40px',
              fontSize: '20px',
              fontFamily: 'Bebas Neue',
              background: '#80A2CB',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer'
            }}
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="debate-room-container">
      {/* Background */}
      <img 
        src={backgroundUrl} 
        alt="Debate Room Background" 
        className="debate-room-background"
      />

      {/* SCALED CONTENT WRAPPER - 70% */}
      <div className="debate-content-wrapper">
        {/* PNG CONTAINERS */}
        
        {/* Header Container - PNG only */}
        <div className="debate-header-container">
          <img src={elementAUrl} alt="Header" className="debate-header-background" />
        </div>

        {/* Analysis Loading Bar */}
        <div className={`analysis-loading-bar ${isAnalyzing ? 'active' : ''}`}>
          <div className="analysis-loading-progress"></div>
        </div>

        {/* Thesis Container - PNG only */}
        <div className="thesis-container">
          <img src={elementAUrl} alt="Thesis" className="thesis-background" />
        </div>

        {/* Time Container Left + Labels */}
        <div className={`time-container-left ${argumentsSubmitted ? 'expanded' : ''} ${getTimeClass(timeRemainingA)}`}>
          <img src={elementAUrl} alt="Time Left" className="element-background" />
          <div className="time-label-left">TIME</div>
          <div className="time-value-left">{formatTime(timeRemainingA)}</div>
        </div>

        {/* Score Container Left + Labels */}
        <div className={`score-container-left ${argumentsSubmitted ? 'expanded' : ''} ${getScoreClass(debate.score_a || 0)}`}>
          <img src={elementAUrl} alt="Score Left" className="element-background" />
          <div className="score-label-left">SCORE</div>
          <div className="score-value-left">{debate.score_a > 0 ? `+${debate.score_a}` : debate.score_a || 0}</div>
        </div>

        {/* Time Container Right + Labels */}
        <div className={`time-container-right ${argumentsSubmitted ? 'expanded' : ''} ${getTimeClass(timeRemainingB)}`}>
          <img src={elementAUrl} alt="Time Right" className="element-background" />
          <div className="time-label-right">TIME</div>
          <div className="time-value-right">{formatTime(timeRemainingB)}</div>
        </div>

        {/* Score Container Right + Labels */}
        <div className={`score-container-right ${argumentsSubmitted ? 'expanded' : ''} ${getScoreClass(debate.score_b || 0)}`}>
          <img src={elementAUrl} alt="Score Right" className="element-background" />
          <div className="score-label-right">SCORE</div>
          <div className="score-value-right">{debate.score_b > 0 ? `+${debate.score_b}` : debate.score_b || 0}</div>
        </div>

        {/* FIXED TEXT ELEMENTS - Absolute from canvas */}
        <div className="total-time-value">{formatTime(totalTimeRemaining)}</div>
        <div className="total-time-label">TOTAL TIME</div>
        
        {/* Protagonist Names with two-line AI format */}
        <div className="protagonist-a-name">
          {playerAName}
          {opponentModel && localStorage.getItem('participant_role') === 'protagonist_b' && (
            <div className="ai-model-line">{opponentModel}</div>
          )}
        </div>
        <div className="protagonist-b-name">
          {playerBName}
          {opponentModel && localStorage.getItem('participant_role') === 'protagonist_a' && (
            <div className="ai-model-line">{opponentModel}</div>
          )}
        </div>
        
        <div className="thesis-text">{debate.thesis?.substring(0, 200) || 'Loading thesis...'}</div>

        {/* ARGUMENTS BUBBLES */}
        <div className="arguments-container" ref={argumentsContainerRef}>
          {debateArguments.map((arg, index) => {
            const isMyArgument = arg.session_id === localStorage.getItem('session_id');
            const isProtagonistA = arg.participant_role === 'protagonist_a';
            
            // Player bubbles are RIGHT-aligned (rechtslastig), Opponent bubbles are LEFT-aligned (linkslastig)
            const bubbleClass = isMyArgument ? 'bubble-right' : 'bubble-left';
            
            // Extract score from moderation_result
            const argumentScore = arg.moderation_result?.score?.total_score || 0;
            const scoreClass = argumentScore > 0 ? 'positive' : argumentScore < 0 ? 'negative' : 'neutral';
            
            // Extract analysis details from moderation_result
            const summary = arg.moderation_result?.summary || '';
            const aiFeedback = arg.moderation_result?.ai_feedback || '';
            const factCheck = arg.moderation_result?.fact_check_result || '';
            const logicAnalysis = arg.moderation_result?.logic_analysis || '';
            const relevanceAnalysis = arg.moderation_result?.relevance_analysis || '';
            const feasibilityAnalysis = arg.moderation_result?.feasibility_analysis || '';
            
            // Check if this argument is showing analysis
            const showingAnalysis = expandedAnalysis === arg.id;
            
            return (
              <div 
                key={arg.id || index}
                className={`argument-bubble ${bubbleClass} ${isMyArgument ? 'my-argument' : 'opponent-argument'} ${showingAnalysis ? 'showing-analysis' : ''}`}
              >
                <div className="bubble-main-content">
                  <div className="bubble-text-area">
                    <div className="bubble-author-row">
                      <div className="bubble-author">
                        {isProtagonistA ? playerAName : playerBName}
                      </div>
                      {!showingAnalysis && (
                        <div className="font-size-controls bubble-font-controls">
                          <button 
                            className="font-size-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              setBubbleFontSize(prev => Math.min(prev + 2, 28));
                            }}
                            title="Schrift vergrößern"
                          >
                            +
                          </button>
                          <button 
                            className="font-size-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              setBubbleFontSize(prev => Math.max(prev - 2, 12));
                            }}
                            title="Schrift verkleinern"
                          >
                            −
                          </button>
                        </div>
                      )}
                    </div>
                    
                    {/* Toggle between Argument Content and Analysis */}
                    {!showingAnalysis ? (
                      <div className="bubble-content" style={{ fontSize: `${bubbleFontSize}px` }}>
                        {arg.content}
                      </div>
                    ) : (
                      <div className="bubble-analysis" style={{ fontSize: `${analysisFontSize}px` }}>
                        <div className="analysis-header">
                          <span>Argumentanalyse</span>
                          <div className="font-size-controls">
                            <button 
                              className="font-size-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                setAnalysisFontSize(prev => Math.min(prev + 2, 24));
                              }}
                              title="Schrift vergrößern"
                            >
                              +
                            </button>
                            <button 
                              className="font-size-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                setAnalysisFontSize(prev => Math.max(prev - 2, 10));
                              }}
                              title="Schrift verkleinern"
                            >
                              −
                            </button>
                          </div>
                        </div>
                        
                        {/* Score */}
                        <div className="analysis-section">
                          <strong>Score:</strong> <span className={`score-display ${scoreClass}`}>
                            {argumentScore > 0 ? `+${argumentScore}` : argumentScore}
                          </span>
                        </div>
                        
                        {/* Short Summary */}
                        {summary && (
                          <div className="analysis-section">
                            <strong>Kurzzusammenfassung:</strong>
                            <div className="analysis-content">
                              {summary}
                            </div>
                          </div>
                        )}
                        
                        {/* Detailed Analysis (AI Feedback) */}
                        {aiFeedback && (
                          <div className="analysis-section">
                            <strong>Detaillierte Analyse:</strong>
                            <div className="analysis-content">
                              {aiFeedback}
                            </div>
                          </div>
                        )}
                        
                        {/* Fact Check */}
                        {factCheck && (
                          <div className="analysis-section">
                            <strong>Faktencheck:</strong>
                            <div className="analysis-content">
                              {factCheck}
                            </div>
                          </div>
                        )}
                        
                        {/* Logic Analysis */}
                        {logicAnalysis && (
                          <div className="analysis-section">
                            <strong>Logikanalyse:</strong>
                            <div className="analysis-content">
                              {logicAnalysis}
                            </div>
                          </div>
                        )}
                        
                        {/* Relevance Analysis */}
                        {relevanceAnalysis && (
                          <div className="analysis-section">
                            <strong>Relevanzanalyse:</strong>
                            <div className="analysis-content">
                              {relevanceAnalysis}
                            </div>
                          </div>
                        )}
                        
                        {/* Feasibility Analysis */}
                        {feasibilityAnalysis && (
                          <div className="analysis-section">
                            <strong>Machbarkeitsanalyse:</strong>
                            <div className="analysis-content">
                              {feasibilityAnalysis}
                            </div>
                          </div>
                        )}
                        
                        {/* Fallback if no details available */}
                        {!summary && !aiFeedback && !factCheck && !logicAnalysis && !relevanceAnalysis && !feasibilityAnalysis && (
                          <div className="analysis-content" style={{ fontStyle: 'italic', color: '#888' }}>
                            Keine detaillierte Analyse verfügbar.
                          </div>
                        )}
                        
                        {/* Feedback Button */}
                        <div className="analysis-section" style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid rgba(255,255,255,0.2)' }}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedArgumentForFeedback(arg);
                              setFeedbackModalOpen(true);
                            }}
                            style={{
                              padding: '8px 16px',
                              background: 'rgba(255, 255, 255, 0.1)',
                              border: '1px solid rgba(255, 255, 255, 0.3)',
                              borderRadius: '4px',
                              color: '#000000',
                              cursor: 'pointer',
                              fontSize: '13px',
                              fontFamily: 'Roboto, sans-serif',
                              transition: 'all 0.2s ease',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              justifyContent: 'center'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)'}
                          >
                            <img 
                              src="https://customer-assets.emergentagent.com/job_mobile-tablet-desk/artifacts/ax0ktd0f_feedback_icon.svg"
                              alt="Feedback"
                              style={{ width: '48px', height: '48px' }}
                            />
                            Feedback zur Bewertung geben
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Local Score Button with Toggle */}
                  <button 
                    className={`bubble-score-btn ${scoreClass}`}
                    onClick={() => {
                      // Toggle between showing argument and analysis
                      setExpandedAnalysis(showingAnalysis ? null : arg.id);
                    }}
                    title={showingAnalysis ? "Zurück zum Argument" : "Klicken für detaillierte Analyse"}
                  >
                    <div className="score-value">
                      {argumentScore > 0 ? `+${argumentScore}` : argumentScore}
                    </div>
                    <div className="score-label">{showingAnalysis ? "ZURÜCK" : "SCORE"}</div>
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* INPUT AREA - Moved outside wrapper */}
      </div>

      {/* INPUT AREA - OUTSIDE SCALED WRAPPER */}
      <div className="debate-input-area-fixed" ref={inputAreaRef}>
        {/* Textarea */}
        <textarea
          ref={textareaRef}
          className={`debate-textarea debate-textarea-${textareaTheme}`}
          placeholder="Enter your argument here..."
          value={newArgument}
          onChange={(e) => {
            const value = e.target.value;
            setNewArgument(value);
            
            // Start animation and timer on first keystroke
            if (!hasStartedTyping && value.length > 0) {
              setHasStartedTyping(true);
              setArgumentsSubmitted(true);
              setIsTimerRunning(true);
            }
          }}
          maxLength={500}
          disabled={submitting || aiStatus.show}
        />

        {/* Button Row */}
        <div className="debate-button-row">
          {/* Keyboard/Microphone Toggle */}
          <button 
            className="debate-btn debate-btn-input-mode"
            onClick={() => {
              if (inputMode === 'keyboard') {
                setInputMode('microphone');
                handleMicrophoneClick();
              } else {
                setInputMode('keyboard');
                if (isRecording) {
                  handleMicrophoneClick(); // Stop recording
                }
              }
            }}
          >
            <div className="btn-icon-area">
              <img 
                src={inputMode === 'keyboard' 
                  ? 'https://customer-assets.emergentagent.com/job_debatefy-freemium/artifacts/a87tynmh_key.svg'
                  : 'https://customer-assets.emergentagent.com/job_debatefy-freemium/artifacts/9vfj6vbv_mic.svg'
                }
                alt={inputMode === 'keyboard' ? 'Keyboard' : 'Microphone'}
                className={`btn-icon-svg ${inputMode === 'keyboard' ? 'icon-keyboard' : ''}`}
              />
            </div>
            <div className="btn-text-area">
              {inputMode === 'keyboard' ? 'KEYBOARD' : isRecording ? 'RECORDING...' : 'MICROPHONE'}
            </div>
          </button>

          {/* Theme Toggle Button */}
          <button 
            className={`debate-btn debate-btn-theme ${textareaTheme === 'dark' ? 'theme-dark' : 'theme-light'}`}
            onClick={() => setTextareaTheme(textareaTheme === 'dark' ? 'light' : 'dark')}
          >
            <div className="btn-icon-area">
              <img 
                src={textareaTheme === 'dark'
                  ? 'https://customer-assets.emergentagent.com/job_debatefy-freemium/artifacts/0t408t43_LightMode.svg'
                  : 'https://customer-assets.emergentagent.com/job_debatefy-freemium/artifacts/zt6gtshi_darkmode.svg'
                }
                alt={textareaTheme === 'dark' ? 'Light Mode' : 'Dark Mode'}
                className="btn-icon-svg"
              />
            </div>
            <div className="btn-text-area">
              {textareaTheme === 'dark' ? 'LIGHT MODE' : 'DARK MODE'}
            </div>
          </button>

          {/* Pause Button - Visible for all, functional for Pro/Unlimited */}
          <button 
            className={`debate-btn debate-btn-pause ${!['pro', 'unlimited'].includes(userTier) ? 'btn-locked' : ''}`}
            onClick={handlePauseDebate}
            disabled={isPausing}
            title={!['pro', 'unlimited'].includes(userTier) ? 'Upgrade to Pro to pause debates' : 'Pause debate and continue later'}
          >
            <div className="btn-content">
              {isPausing ? '⏳' : '⏸️'} {isPausing ? 'PAUSING...' : 'PAUSE'}
              {!['pro', 'unlimited'].includes(userTier) && <span className="pro-badge">PRO</span>}
            </div>
          </button>

          {/* Stop Well Argued Button */}
          <button 
            className="debate-btn debate-btn-stop"
            onClick={handleStopWellArgued}
          >
            <div className="btn-content">STOP WELL ARGUED</div>
          </button>

          {/* Submit Button */}
          <button 
            className="debate-btn debate-btn-submit"
            onClick={handleSubmitArgument}
            disabled={submitting || !newArgument.trim() || aiStatus.show}
          >
            <div className="btn-content">{submitting ? 'SUBMITTING...' : 'SUBMIT'}</div>
          </button>
        </div>
      </div>

      {/* Feedback Modal */}
      <FeedbackModal
        isOpen={feedbackModalOpen}
        onClose={() => setFeedbackModalOpen(false)}
        argument={selectedArgumentForFeedback}
        debateId={debateId}
      />

      {/* Pause Success Modal with Resume Link */}
      {showPauseModal && (
        <div className="pause-modal-overlay">
          <div className="pause-modal">
            <div className="pause-modal-icon">⏸️</div>
            <h2>Debate Paused</h2>
            <p>Share this link with your opponent to resume the debate:</p>
            
            <div className="resume-link-box">
              <input 
                type="text" 
                value={resumeLink} 
                readOnly 
                className="resume-link-input"
              />
              <button 
                className={`copy-link-btn ${linkCopied ? 'copied' : ''}`}
                onClick={() => {
                  navigator.clipboard.writeText(resumeLink);
                  setLinkCopied(true);
                  toast.success('✅ Link copied! You can now leave this page.');
                }}
              >
                {linkCopied ? '✅ Copied!' : '📋 Copy Link'}
              </button>
            </div>
            
            <p className="pause-expiry-note">
              ⏰ This link expires in <strong>{daysRemaining} days</strong>
            </p>
            
            {!linkCopied && (
              <p className="pause-warning">
                ⚠️ Please copy the link before leaving!
              </p>
            )}
            
            {linkCopied && (
              <div className="pause-modal-actions">
                <button 
                  className="pause-modal-btn primary"
                  onClick={() => navigate('/')}
                >
                  Go to Home
                </button>
                <button 
                  className="pause-modal-btn secondary"
                  onClick={() => navigate('/archive')}
                >
                  View Archive
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Resume Overlay for Paused Debates */}
      {showResumeOverlay && (
        <div className="resume-overlay">
          <div className="resume-modal">
            <div className="resume-modal-icon">⏸️</div>
            <h2>Paused Debate</h2>
            <p className="resume-thesis">"{debate?.thesis}"</p>
            
            <div className="resume-stats">
              <div className="resume-stat">
                <span className="stat-label">Time A</span>
                <span className="stat-value">{Math.floor(timeRemainingA / 60)}:{String(timeRemainingA % 60).padStart(2, '0')}</span>
              </div>
              <div className="resume-stat">
                <span className="stat-label">Time B</span>
                <span className="stat-value">{Math.floor(timeRemainingB / 60)}:{String(timeRemainingB % 60).padStart(2, '0')}</span>
              </div>
              <div className="resume-stat">
                <span className="stat-label">Total</span>
                <span className="stat-value">{Math.floor(totalTimeRemaining / 60)}:{String(totalTimeRemaining % 60).padStart(2, '0')}</span>
              </div>
            </div>
            
            <button 
              className="resume-btn"
              onClick={handleResumeDebate}
            >
              ▶️ Resume Debate
            </button>
            
            <button 
              className="resume-btn-secondary"
              onClick={() => navigate('/')}
            >
              Back to Home
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DebateRoomNew;
